import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2, Check } from 'lucide-react';
import { formatCurrency, formatDate, today, canModifyRecord, isAdminUser, approvalBadge } from '../utils/helpers';

const empty = { vehicle_id:'', fuel_date:today(), odometer_reading:'', liters:0, rate:0, total_amount:0, fuel_station:'', remarks:'' };

export default function Fuel() {
  const [list, setList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const admin = isAdminUser();

  const load = () => api.get('/fuel').then(r => setList(r.data.data || []));
  useEffect(() => { load(); api.get('/vehicles').then(r => setVehicles(r.data.data || [])); }, []);

  const updateCalc = (liters, rate) => {
    const total = parseFloat(liters||0) * parseFloat(rate||0);
    setForm(f => ({...f, liters, rate, total_amount: total.toFixed(2)}));
  };

  const save = async () => {
    if (!form.vehicle_id) return toast.error('Vehicle required');
    try { const r = await api.post('/fuel', form); toast.success(r.data.message || 'Logged'); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const del = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete('/fuel/' + id); toast.success('Deleted'); load(); } catch (e) { toast.error('Error'); } };
  const approve = async (id) => { if (!window.confirm('Approve this fuel entry?')) return; try { await api.post('/fuel/' + id); toast.success('Approved'); load(); } catch (e) { toast.error(e?.response?.data?.message || 'Error'); } };

  // Aggregate totals are based on approved rows only
  const approved = list.filter(r => r.approval_status === 'approved');
  const total = approved.reduce((s, r) => s + parseFloat(r.total_amount || 0), 0);
  const totalLiters = approved.reduce((s, r) => s + parseFloat(r.liters || 0), 0);
  const pendingCount = list.filter(r => r.approval_status === 'pending').length;

  return (
    <div className="card">
      <div className="card-header">
        <h3>Fuel Log ({list.length}) — Total: {formatCurrency(total)} · {totalLiters.toFixed(2)} Ltrs{pendingCount > 0 && <span className="badge badge-orange" style={{marginLeft:8,fontSize:11}}>{pendingCount} pending</span>}</h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={14}/> Log Fuel</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Vehicle</th><th>Odometer</th><th className="td-num">Liters</th><th className="td-num">Rate</th><th className="td-num">Amount</th><th>Station</th><th>Approval</th><th>Actions</th></tr></thead>
          <tbody>
            {list.length ? list.map(f => {
              const badge = approvalBadge(f.approval_status);
              const canMod = canModifyRecord(f);
              const isPending = f.approval_status === 'pending';
              return (
                <tr key={f.id} style={isPending ? {background:'#fffbeb'} : {}}>
                  <td>{formatDate(f.fuel_date)}</td>
                  <td><strong>{f.vehicle_no}</strong></td>
                  <td>{f.odometer_reading || '-'}</td>
                  <td className="td-num">{f.liters}</td>
                  <td className="td-num">{formatCurrency(f.rate)}</td>
                  <td className="td-num">{formatCurrency(f.total_amount)}</td>
                  <td>{f.fuel_station || '-'}</td>
                  <td>
                    <span className={badge.cls}>{badge.label}</span>
                    {f.created_by_name && <small style={{display:'block',color:'#94a3b8',fontSize:10,marginTop:2}}>by {f.created_by_name}</small>}
                  </td>
                  <td>
                    {admin && isPending && <button className="btn btn-sm btn-green" onClick={()=>approve(f.id)} title="Approve"><Check size={12}/></button>}
                    {' '}
                    {canMod && <button className="btn btn-sm btn-red" onClick={()=>del(f.id)} title="Delete"><Trash2 size={12}/></button>}
                    {!canMod && !isPending && <span style={{color:'#94a3b8',fontSize:11}}>Locked</span>}
                  </td>
                </tr>
              );
            }) : <tr><td colSpan="9" className="empty">No fuel records</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title="Log Fuel"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>Save</button>
        </>}>
        {!admin && (
          <div style={{background:'#fef3c7',border:'1px solid #fcd34d',padding:'10px 12px',borderRadius:8,marginBottom:14,fontSize:13,color:'#92400e'}}>
            Your entry will be saved as <strong>pending</strong> until an admin approves it.
          </div>
        )}
        <div className="form-grid">
          <div className="form-group"><label>Vehicle *</label>
            <select value={form.vehicle_id} onChange={e=>setForm({...form, vehicle_id:e.target.value})}>
              <option value="">-- Select --</option>
              {vehicles.map(v=><option key={v.id} value={v.id}>{v.vehicle_no}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Date</label><input type="date" value={form.fuel_date} onChange={e=>setForm({...form, fuel_date:e.target.value})}/></div>
          <div className="form-group"><label>Odometer (KM)</label><input type="number" value={form.odometer_reading} onChange={e=>setForm({...form, odometer_reading:e.target.value})}/></div>
          <div className="form-group"><label>Liters</label><input type="number" step="0.01" value={form.liters} onChange={e=>updateCalc(e.target.value, form.rate)}/></div>
          <div className="form-group"><label>Rate per Liter</label><input type="number" step="0.01" value={form.rate} onChange={e=>updateCalc(form.liters, e.target.value)}/></div>
          <div className="form-group"><label>Total Amount</label><input type="number" value={form.total_amount} onChange={e=>setForm({...form, total_amount:e.target.value})}/></div>
          <div className="form-group full-width"><label>Fuel Station</label><input value={form.fuel_station} onChange={e=>setForm({...form, fuel_station:e.target.value})}/></div>
          <div className="form-group full-width"><label>Remarks</label><textarea value={form.remarks} onChange={e=>setForm({...form, remarks:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
